// React / Next
import { useEffect } from 'react';
import { Link } from '@tanstack/react-router';

// Components
import { ArticleHero } from '../../components/ArticleHero/ArticleHero';
import { ArticleSidebar } from '../../components/ArticleSidebar/ArticleSidebar';
import { ArticlePortableText } from '../../components/ArticlePortableText/ArticlePortableText';
import { ArticleHubSpotForm } from '../../components/ArticleHubSpotForm/ArticleHubSpotForm';
import { RelatedArticles } from '../../components/RelatedArticles/RelatedArticles';
import { HireArchieCTA } from '../../components/HireArchieCTA/HireArchieCTA';
import { SEO } from '../../components/SEO/SEO';

// Motion
import { motion } from 'motion/react';

// Hooks
import { useTrackEvent } from '../../hooks/useTrackEvent';

// Types
import type { Article } from '../../lib/sanity/types';

// Types
type ArticleDetailPageProps = {
  article: Article | null;
};

/**
 * ArticleDetailPage - Full article detail page
 * Displays the complete article with sidebar, content, and related articles
 *
 * Layout from Figma (node 227:1600):
 * - Max width: 1016px
 * - Content width: 804px
 * - Sidebar width: 184px
 * - Gap between content and sidebar: 28px
 *
 * Vertical spacing (using design system tokens):
 * - Go Back → Hero: 48px (--spacing-48)
 * - Hero → Featured Image: 32px (--spacing-32)
 * - Featured Image → Content: 48px (--spacing-48)
 * - Content/Sidebar gap: 24px (--spacing-24)
 * - Content section → Related Articles: 64px (--spacing-64)
 * - Related Articles → Hire Archie: 64px (--spacing-64)
 */
export const ArticleDetailPage = ({ article }: ArticleDetailPageProps) => {
  const trackEvent = useTrackEvent();

  // Format categories as tags string
  const formatCategoriesAsTags = (categories: Article['categories']): string => {
    return categories?.map((cat) => cat.title).join(' | ') || '';
  };

  // Format article type for display
  const formatArticleType = (type: Article['articleType']): string => {
    const typeMap: Record<string, string> = {
      article: 'Article',
      playbook: 'Playbook',
      news: 'News',
    };
    return typeMap[type] || type;
  };

  // Transform related articles for RelatedArticles component
  const transformRelatedArticles = (relatedArticles: Article['relatedArticles']) => {
    if (!relatedArticles?.length) return [];

    return relatedArticles.map((related) => ({
      id: related._id,
      imageSrc: related.featuredImage?.url || '/img/placeholder.jpg',
      imageAlt: related.featuredImage?.alt,
      title: related.title,
      excerpt: related.excerpt || '',
      author: related.author?.name || 'Unknown',
      tags: related.categories?.map((cat) => cat.title).join(' | ') || '',
      href: `/resources/${related.slug}`,
    }));
  };

  useEffect(() => {
    if (!article) return;

    trackEvent('article_viewed', {
      slug: article.slug,
      title: article.title,
      type: article.articleType,
      author: article.author?.name,
      category: article.categories?.map((cat) => cat.title).join(' | '),
    });
  }, [article, trackEvent]);

  // Loading state
  if (!article) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen w-full bg-white">
        <p className="font-body text-[16px] text-gray-600">Article not found</p>
        <Link
          to="/resources"
          className="mt-[var(--spacing-16)] font-body text-[16px] text-blue-800 hover:text-orange-800 transition-colors"
        >
          ← Back to Resources
        </Link>
      </div>
    );
  }

  const tags = formatCategoriesAsTags(article.categories);
  const articleType = formatArticleType(article.articleType);
  const relatedArticles = transformRelatedArticles(article.relatedArticles);

  return (
    <div className="flex flex-col items-center w-full bg-white">
      <SEO
        title={article.seo?.metaTitle || article.title}
        description={article.seo?.metaDescription || article.subtitle || article.excerpt}
        keywords={tags}
        ogImage={article.seo?.ogImage?.url || article.featuredImage?.url}
        ogType="article"
        canonicalUrl={`/resources/${article.slug}`}
      />

      {/* Main Content Container */}
      <div
        className="flex flex-col w-full max-w-[1016px] max-md:px-4"
        style={{ padding: '48px 24px' }}
      >
        {/* Go Back Button */}
        <motion.div
          className="flex justify-center w-full"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <Link
            to="/resources"
            className="inline-flex items-center font-body text-[16px] leading-[24px] text-blue-1000 hover:text-blue-800 transition-colors duration-200"
            style={{ gap: '8px' }}
          >
            <ArrowLeftIcon />
            Go back
          </Link>
        </motion.div>

        {/* Hero Section - 48px from Go Back */}
        <motion.div
          className="w-full"
          style={{ marginTop: '48px' }}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          <ArticleHero
            title={article.title}
            subtitle={article.subtitle}
            author={article.author?.name || 'Unknown'}
            tags={tags}
            shareUrl={`/resources/${article.slug}`}
          />
        </motion.div>

        {/* Banner Image - 32px from Hero (uses bannerImage with featuredImage fallback) */}
        {(article.bannerImage?.url || article.featuredImage?.url) && (
          <motion.div
            className="w-full"
            style={{ marginTop: '32px' }}
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <img
              src={article.bannerImage?.url || article.featuredImage?.url}
              alt={article.bannerImage?.alt || article.featuredImage?.alt || article.title}
              className="w-full object-cover"
              style={{ height: '303px', borderRadius: '12px' }}
            />
          </motion.div>
        )}

        {/* Content Section with Sidebar - 48px from Featured Image */}
        <div
          className="flex w-full max-lg:flex-col items-start"
          style={{ marginTop: '48px', gap: '28px' }}
        >
          {/* Main Content - 804px width in Figma */}
          <motion.div
            className="flex-1 min-w-0 w-full"
            style={{ maxWidth: '804px' }}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            {/* Article Body from Sanity */}
            {article.body && article.body.length > 0 && (
              <ArticlePortableText value={article.body} />
            )}

            {/* HubSpot Form - if available from Sanity and has a formId */}
            {article.hubspotForm?.formId && (
              <div style={{ margin: '48px 0' }}>
                <ArticleHubSpotForm formConfig={article.hubspotForm} />
              </div>
            )}
          </motion.div>

          {/* Sidebar - 184px width, aligned to start of main content */}
          <motion.div
            className="shrink-0 max-lg:max-w-full max-lg:order-first self-start"
            style={{ width: '184px' }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <div className="sticky" style={{ top: '100px', paddingTop: 0 }}>
              <ArticleSidebar
                author={article.author?.name || 'Unknown'}
                categories={article.categories?.map((cat) => cat.title) || []}
                type={articleType}
              />
            </div>
          </motion.div>
        </div>

        {/* Related Articles - 64px from content section */}
        {relatedArticles.length > 0 && (
          <motion.div
            className="w-full"
            style={{ marginTop: '64px' }}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.6 }}
          >
            <RelatedArticles articles={relatedArticles} />
          </motion.div>
        )}
      </div>

      {/* Hire Archie CTA - Full width, 64px from Related Articles */}
      <div className="w-full" style={{ marginTop: '64px' }}>
        <HireArchieCTA />
      </div>
    </div>
  );
};

// Arrow Icon Component
const ArrowLeftIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <line x1="19" y1="12" x2="5" y2="12" />
    <polyline points="12 19 5 12 12 5" />
  </svg>
);
